import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, Settings, BarChart3, Monitor, BookOpen, Briefcase, Menu, X } from 'lucide-react';

const COMPANIES = [
  {
    name: 'B2B HUB',
    logo: 'https://customer-assets.emergentagent.com/job_realestateanalytics/artifacts/3ki3n9eb_1.png',
    url: 'https://b2bhub.ltd'
  },
  {
    name: 'DEZINFECTOR',
    logo: 'https://customer-assets.emergentagent.com/job_realestateanalytics/artifacts/bpcdadi5_2.png',
    url: 'https://dezinfector.com.ua'
  },
  {
    name: 'Astra Terminal',
    logo: 'https://customer-assets.emergentagent.com/job_realestateanalytics/artifacts/av6xoxk1_3.png',
    url: 'https://astraterminal.net'
  }
];

const SERVICES = [
  { icon: TrendingUp, title: 'Marketing', desc: 'Full-cycle marketing strategy and execution' },
  { icon: Settings, title: 'Operations', desc: 'Streamlining processes for peak efficiency' },
  { icon: Monitor, title: 'System Renovation', desc: 'Modernizing legacy systems and workflows' },
  { icon: BookOpen, title: 'Accounting', desc: 'Financial reporting and tax optimization' },
  { icon: Briefcase, title: 'IT Infrastructure', desc: 'Robust tech stack and digital transformation' },
  { icon: BarChart3, title: 'Strategy', desc: 'Data-driven growth planning and scaling' }
];

const NAV_LINKS = [
  { label: 'Companies', href: '#companies' },
  { label: 'Partnership', href: '#partnership' }
];

const Landing = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollTo = (id) => {
    setMobileMenuOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B1437] via-[#1a2556] to-[#0B1437]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0B1437]/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <img
            src="https://customer-assets.emergentagent.com/job_realestateanalytics/artifacts/lhvv6y36_Menelaus%20Holding.png"
            alt="Menelaus Holding"
            className="w-10 h-10 sm:w-12 sm:h-12"
          />

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollTo(link.href.slice(1))}
                className="text-slate-300 hover:text-white text-sm font-medium transition-colors"
              >
                {link.label}
              </button>
            ))}
            <motion.button
              onClick={() => navigate('/login')}
              className="px-8 py-2.5 bg-gradient-to-r from-[#FF6B35] to-[#F7931E] text-white text-sm font-bold rounded-full shadow-lg transition-all duration-300 hover:scale-105"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{ boxShadow: '0 0 20px rgba(255, 107, 53, 0.4)' }}
              data-testid="header-login-btn"
            >
              Login
            </motion.button>
          </nav>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-white p-2"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile menu dropdown */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden overflow-hidden bg-[#0B1437]/95 backdrop-blur-xl"
              data-testid="mobile-menu"
            >
              <div className="px-4 py-4 space-y-3">
                {NAV_LINKS.map((link) => (
                  <button
                    key={link.href}
                    onClick={() => scrollTo(link.href.slice(1))}
                    className="block w-full text-left text-slate-300 hover:text-white text-base font-medium py-2 px-3 rounded-lg hover:bg-white/5 transition-colors"
                  >
                    {link.label}
                  </button>
                ))}
                <button
                  onClick={() => { setMobileMenuOpen(false); navigate('/login'); }}
                  className="w-full py-3 bg-gradient-to-r from-[#FF6B35] to-[#F7931E] text-white text-sm font-bold rounded-xl shadow-lg mt-2"
                  data-testid="mobile-login-btn"
                >
                  Login
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main className="pt-24 sm:pt-32 pb-16 sm:pb-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16 sm:mb-20"
          >
            <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold text-white mb-3 sm:mb-4" data-testid="hero-title">
              Menelaus Holding
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-[#FF6B35] font-medium">
              Your Corporate Umbrella
            </p>
          </motion.div>

          {/* Our Companies */}
          <motion.section
            id="companies"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mb-24 sm:mb-32"
            data-testid="companies-section"
          >
            <h2 className="text-center text-xs sm:text-sm font-bold uppercase tracking-[0.3em] text-slate-400 mb-8 sm:mb-12">
              Our Companies
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-8 max-w-4xl mx-auto">
              {COMPANIES.map((company, index) => (
                <motion.a
                  key={company.name}
                  href={company.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="bg-white rounded-xl p-4 hover:shadow-xl transition-all duration-300 flex items-center justify-center"
                  data-testid={`company-${company.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <img
                    src={company.logo}
                    alt={company.name}
                    className="h-12 sm:h-16 w-auto object-contain"
                  />
                </motion.a>
              ))}
            </div>
          </motion.section>

          {/* Get Support from MH */}
          <motion.section
            id="partnership"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-5xl mx-auto"
            data-testid="support-section"
          >
            <h2 className="text-center text-xs sm:text-sm font-bold uppercase tracking-[0.3em] text-slate-400 mb-3 sm:mb-4">
              Get Support from MH
            </h2>
            <p className="text-center text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6">
              Partner with Menelaus Holding
            </p>
            <p className="text-center text-slate-400 text-sm sm:text-base md:text-lg max-w-2xl mx-auto mb-10 sm:mb-16 leading-relaxed px-2">
              We invest in companies with stable cash flow. In exchange for <span className="text-[#FF6B35] font-semibold">5-30%</span> equity — depending on the scope — we take full responsibility for key operational areas so you can focus on what you do best.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 mb-10 sm:mb-16">
              {SERVICES.map((service, index) => {
                const Icon = service.icon;
                return (
                  <motion.div
                    key={service.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.08 }}
                    className="card-premium rounded-2xl p-5 sm:p-6 group"
                    data-testid={`service-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <div className="w-10 h-10 rounded-xl bg-[#FF6B35]/10 flex items-center justify-center mb-3 sm:mb-4 group-hover:bg-[#FF6B35]/20 transition-colors">
                      <Icon size={20} className="text-[#FF6B35]" />
                    </div>
                    <h3 className="text-white font-semibold text-base mb-1 sm:mb-1.5">{service.title}</h3>
                    <p className="text-slate-400 text-sm leading-relaxed">{service.desc}</p>
                  </motion.div>
                );
              })}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="card-premium rounded-2xl p-6 sm:p-8 text-center"
              data-testid="requirements-callout"
            >
              <p className="text-white text-base sm:text-lg font-semibold mb-2 sm:mb-3">What we look for</p>
              <p className="text-slate-400 text-sm sm:text-base leading-relaxed max-w-2xl mx-auto">
                Your company should demonstrate stable, recurring cash flow. We handle marketing, operations, system renovation, accounting, and IT infrastructure — taking on <span className="text-[#FF6B35] font-semibold">5-30%</span> of equity based on the level of involvement required.
              </p>
            </motion.div>
          </motion.section>
        </div>
      </main>

      <footer className="py-6 sm:py-8">
        <p className="text-center text-slate-500 text-xs sm:text-sm">
          &copy; 2026 Menelaus Holding. All rights reserved.
        </p>
      </footer>
    </div>
  );
};

export default Landing;
